import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DistributorReportTable from '../components/distributor/DistributorReportTable';
import reportService, { saveExportResponse } from '../services/reportService';
import useShippingRequest from '../hooks/useShippingRequest';
import { useT } from '../i18n/LanguageContext';
import '../styles/distributor-admin.css';
import { cashReportMoney } from '../components/distributor-cash/cashMoney';
import { IVR_STATUS_CONFIG } from '../features/ivr/ivrPresentation';

const DATASETS = [
  ['customers', 'Customers'],
  ['orders', 'Orders & resale'],
  ['ivr', 'IVRs'],
  ['shipments', 'Shipments'],
  ['purchases', 'Unit purchases'],
  ['bank', 'Unit ledger'],
  ['cash_bank', 'Cash ledger'],
  ['support', 'Help requests'],
  ['resources', 'Library documents'],
];
const DESCRIPTIONS = {
  customers: 'Current customers, ordering access and resale rates.',
  orders:
    'One row per order item. Saved retail prices are shown when available. Retail amounts never change distributor bank debits.',
  ivr: 'Coverage request statuses for your customers. Patient details are excluded.',
  shipments: 'Order status, tracking and expected delivery for your shipments.',
  purchases: 'Historical product-unit purchases, including purchases not yet credited.',
  bank: 'Historical additions and deductions. Net movement is not available stock.',
  cash_bank: 'Deposits, order charges and reversals. Each balance is saved at that entry.',
  support: 'Request references and statuses. Read messages in Help Center.',
  resources: 'Currently published files. Open original documents in Resources & Tools.',
};
const ROW_NAMES = {
  customers: ['{count} customer', '{count} customers'],
  orders: ['{count} order line', '{count} order lines'],
  ivr: ['{count} request', '{count} requests'],
  shipments: ['{count} shipment', '{count} shipments'],
  purchases: ['{count} purchase', '{count} purchases'],
  bank: ['{count} entry', '{count} entries'],
  cash_bank: ['{count} entry', '{count} entries'],
  support: ['{count} request', '{count} requests'],
  resources: ['{count} document', '{count} documents'],
};
const SEARCH_HINTS = {
  customers: 'Name or address',
  orders: 'Order or customer',
  ivr: 'Request or customer',
  shipments: 'Order or tracking',
  purchases: 'Product or payment',
  bank: 'Product or reference',
  cash_bank: 'Order number',
  support: 'Request or customer',
  resources: 'Title or file name',
};
const STATUS_LABELS = {
  enabled: 'Ordering enabled',
  disabled: 'Ordering disabled',
  submitted: 'Submitted',
  approved: 'Approved',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
  needs_review: 'Needs review',
  covered: IVR_STATUS_CONFIG.covered.label,
  not_covered: IVR_STATUS_CONFIG.not_covered.label,
  rejected: IVR_STATUS_CONFIG.rejected.label,
  pending: 'Pending',
  in_transit: 'In transit',
  prepared: 'Prepared',
  credited: 'Credited',
  purchase: 'Purchase',
  correction: 'Correction',
  debit: 'Debit',
  reversal: 'Reversal',
  deposit: 'Funds added',
  order_debit: 'Order charge',
  order_reversal: 'Order reversal',
  needs_support: 'Needs support',
  waiting_on_user: 'Waiting on requester',
  resolved: 'Resolved',
  published: 'Published',
};
const TOTAL_LABELS = {
  records: 'Matching records',
  rows: 'Matching rows',
  amount: 'Selected amount (USD)',
  amountUnavailable: 'Amounts unavailable',
  units: 'Saved units',
  netUnits: 'Net unit movement',
  netAmount: 'Net cash movement (USD)',
};
const DATE_LABELS = {
  'membership.createdAt': 'Customer added',
  'order.createdAt': 'Order submitted',
  'ivr.createdAt': 'IVR submitted',
  'shipment.createdAt': 'Shipment created',
  'purchase.createdAt': 'Purchase created',
  'entry.createdAt': 'Ledger entry created',
  'request.createdAt': 'Support request created',
  'resource.publishedAt': 'Resource published',
};
const EMPTY = { search: '', status: '', dateFrom: '', dateTo: '' };
const control = 'me-report-control';
const button = 'me-distributor-admin-secondary';
const display = (value) =>
  value === null || value === undefined || value === ''
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
export default function DistributorReports({ adminDistributorId }) {
  const { version } = useShippingRequest('distributor-reports-access');
  return (
    <ReportWorkspace
      key={`${version}:${adminDistributorId || 'own'}`}
      adminDistributorId={adminDistributorId}
    />
  );
}

function ReportWorkspace({ adminDistributorId }) {
  const t = useT();
  const helpId = useId();
  const clearButton = useRef(null);
  const [dataset, setDataset] = useState('customers');
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [format, setFormat] = useState('csv');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');
  const [busy, setBusy] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const query = {
    dataset,
    ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    page,
    limit: 25,
    ...(adminDistributorId ? { distributorId: adminDistributorId } : {}),
  };
  const queryKey = JSON.stringify(query);
  const { begin, version } = useShippingRequest(queryKey);
  const { begin: beginExport, cancel: cancelExport } = useShippingRequest(
    `${queryKey}:${JSON.stringify(draft)}`
  );
  const data = result?.key === queryKey && result?.version === version ? result.data : null;
  const load = useCallback(async () => {
    const request = begin();
    setResult(null);
    setError('');
    try {
      const response = await reportService.searchDistributor(JSON.parse(queryKey), request);
      if (request.isCurrent()) {
        const lastPage = Math.max(1, Math.ceil(response.total / response.limit));
        if (response.page > lastPage) setPage(lastPage);
        else setResult({ key: queryKey, version, data: response });
      }
    } catch {
      if (request.isCurrent()) setError('Could not load this report. Please try again.');
    }
  }, [begin, queryKey, version]);
  useEffect(() => {
    load();
    cancelExport();
    setBusy(false);
    setExportError('');
  }, [load, cancelExport]);
  useEffect(() => {
    cancelExport();
    setBusy(false);
    setExportError('');
  }, [draft, cancelExport]);
  const invalidDates = draft.dateFrom && draft.dateTo && draft.dateFrom > draft.dateTo;
  const pendingFilters = JSON.stringify(draft) !== JSON.stringify(filters);
  const hasFilters = Object.values(filters).some(Boolean);
  const extraFilters = ['status', 'dateFrom', 'dateTo'].filter((key) => draft[key]).length;
  const reportLabel = t(DATASETS.find(([key]) => key === dataset)[1]);
  const resetFilters = () => {
    setDraft(EMPTY);
    setFilters(EMPTY);
    setPage(1);
    clearButton.current?.focus();
  };
  const refresh = () => {
    cancelExport();
    setBusy(false);
    setExportError('');
    load();
  };
  const totals = Object.entries(data?.totals || {}).filter(
    ([key, value]) =>
      !['amountMinor', 'netCents'].includes(key) &&
      !(key === 'amountUnavailable' && String(value) === '0') &&
      !(['records', 'rows'].includes(key) && String(value) === String(data.total))
  );
  const allAmountsUnavailable =
    ['orders', 'purchases'].includes(dataset) &&
    data?.total > 0 &&
    Number(data.totals?.amountUnavailable) >= data.total;
  const download = async () => {
    if (!data || busy || pendingFilters) return;
    const request = beginExport();
    setBusy(true);
    setExportError('');
    try {
      const { page: unusedPage, limit: unusedLimit, ...allMatches } = query;
      const response = await reportService.exportDistributor({ ...allMatches, format }, request);
      if (request.isCurrent()) saveExportResponse(response, `distributor-${dataset}`);
    } catch {
      if (request.isCurrent()) setExportError('Could not export this report. Please try again.');
    } finally {
      if (request.isCurrent()) setBusy(false);
    }
  };
  return (
    <section
      className="me-distributor-admin me-distributor-reports"
      aria-label={t('Distributor Reports')}
    >
      {!adminDistributorId && (
        <PageHeader title="Reports" subtitle="View, filter and export your business records." />
      )}
      <form
        className="me-report-setup"
        aria-label={t('Report filters')}
        onSubmit={(event) => {
          event.preventDefault();
          if (!invalidDates) {
            if (pendingFilters || page !== 1) {
              setFilters({ ...draft });
              setPage(1);
            } else refresh();
          }
        }}
      >
        <label className="me-report-picker">
          {t('Report')}
          <select
            className={control}
            value={dataset}
            onChange={(event) => {
              setDataset(event.target.value);
              setPage(1);
              setDraft(EMPTY);
              setFilters(EMPTY);
            }}
          >
            {[
              ['Business', DATASETS.slice(0, 4)],
              ['Bank & purchases', DATASETS.slice(4, 7)],
              ['Tools', DATASETS.slice(7)],
            ].map(([group, options]) => (
              <optgroup label={t(group)} key={group}>
                {options.map(([value, label]) => (
                  <option key={value} value={value}>
                    {t(label)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="me-report-search">
          {t('Search')}
          <input
            className={control}
            aria-label={t('Search records')}
            value={draft.search}
            maxLength={200}
            placeholder={t(SEARCH_HINTS[dataset])}
            onChange={(event) => setDraft({ ...draft, search: event.target.value })}
          />
        </label>
        <button
          className="me-report-mobile-filters me-distributor-admin-secondary"
          type="button"
          aria-expanded={filtersOpen || Boolean(invalidDates)}
          aria-controls={`${helpId}-filters`}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            tune
          </span>
          {t('Filters')}
          {extraFilters > 0 && <span className="me-report-filter-count">{extraFilters}</span>}
        </button>
        <div
          className="me-report-more"
          id={`${helpId}-filters`}
          data-open={filtersOpen || Boolean(invalidDates)}
        >
          {dataset !== 'resources' && (
            <label className="me-report-status-filter">
              {t(['bank', 'cash_bank'].includes(dataset) ? 'Type' : 'Status')}
              <select
                className={control}
                value={draft.status}
                disabled={!data}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                <option value="">
                  {t(['bank', 'cash_bank'].includes(dataset) ? 'All types' : 'All statuses')}
                </option>
                {(data?.statuses || []).map((status) => (
                  <option value={status} key={status}>
                    {t(STATUS_LABELS[status] || status)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="me-report-date">
            {t('Start date')}
            <input
              className={control}
              type="date"
              value={draft.dateFrom}
              max={draft.dateTo || undefined}
              aria-describedby={`${helpId}-dates${invalidDates ? ` ${helpId}-error` : ''}`}
              aria-invalid={Boolean(invalidDates)}
              onChange={(event) => setDraft({ ...draft, dateFrom: event.target.value })}
            />
          </label>
          <label className="me-report-date">
            {t('End date')}
            <input
              className={control}
              type="date"
              value={draft.dateTo}
              min={draft.dateFrom || undefined}
              aria-describedby={`${helpId}-dates${invalidDates ? ` ${helpId}-error` : ''}`}
              aria-invalid={Boolean(invalidDates)}
              onChange={(event) => setDraft({ ...draft, dateTo: event.target.value })}
            />
          </label>
        </div>
        <div className="me-report-filter-actions">
          <button
            className="me-distributor-admin-primary"
            aria-label={t('Apply filters')}
            disabled={Boolean(invalidDates)}
            type="submit"
          >
            {t('Apply')}
          </button>
          <button
            ref={clearButton}
            className="me-report-clear"
            type="button"
            aria-label={t('Clear filters')}
            onClick={resetFilters}
          >
            {t('Clear')}
          </button>
        </div>
        <p id={`${helpId}-dates`} className="sr-only">
          {data?.dateField &&
            `${t('Dates filter')}: ${t(DATE_LABELS[data.dateField] || data.dateField)}. `}
          {t(
            'Dates use UTC. Both selected days are included; blank dates include all available history.'
          )}
        </p>
        {invalidDates && (
          <p className="me-report-error" id={`${helpId}-error`} role="alert">
            {t('End date must be on or after start date.')}
          </p>
        )}
      </form>
      <section
        className="me-report-results"
        aria-label={t('Report results')}
        aria-busy={!data && !error}
      >
        <div className="me-report-toolbar">
          <div className="me-report-result-title">
            <div className="me-report-title-line">
              <h2>{reportLabel}</h2>
              {data && (
                <span role="status" className="me-report-count">
                  {t(ROW_NAMES[dataset][data.total === 1 ? 0 : 1], { count: data.total })}
                </span>
              )}
              <details
                className="me-report-about"
                key={dataset}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget))
                    event.currentTarget.open = false;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.currentTarget.open = false;
                    event.currentTarget.querySelector('summary').focus();
                  }
                }}
              >
                <summary title={t('About this report')}>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    info
                  </span>
                  <span className="sr-only">{t('About this report')}</span>
                </summary>
                <div className="me-report-about-content">
                  <p>{t(DESCRIPTIONS[dataset])}</p>
                  {data?.dateField && (
                    <p>
                      {t('Dates filter')}: {t(DATE_LABELS[data.dateField] || data.dateField)} · UTC
                    </p>
                  )}
                  <p>
                    {t(
                      'Dates use UTC. Both selected days are included; blank dates include all available history.'
                    )}
                  </p>
                </div>
              </details>
            </div>
          </div>
          <div className="me-report-export">
            <button
              type="button"
              className="me-report-refresh"
              title={t('Refresh')}
              aria-label={t('Refresh')}
              aria-disabled={!data || busy}
              onClick={() => {
                if (data && !busy) refresh();
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                refresh
              </span>
            </button>
            <label>
              <span className="sr-only">{t('File format')}</span>
              <select
                className={control}
                value={format}
                disabled={busy}
                onChange={(event) => setFormat(event.target.value)}
              >
                <option value="csv">CSV</option>
                <option value="xlsx">Excel</option>
              </select>
            </label>
            <button
              className="me-distributor-admin-primary"
              disabled={!data || busy || pendingFilters || !data.total}
              aria-label={t(busy ? 'Downloading...' : 'Export all matching rows')}
              aria-describedby={pendingFilters ? `${helpId}-pending` : undefined}
              onClick={download}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                download
              </span>
              {t(busy ? 'Downloading...' : 'Export')}
            </button>
          </div>
        </div>
        {pendingFilters && (
          <p id={`${helpId}-pending`} className="me-report-pending" role="status">
            {t('Showing the last applied filters.')} {t('Apply your changes before exporting.')}
          </p>
        )}
        {error ? (
          <p role="alert" className="me-report-state me-report-error">
            {t(error)}{' '}
            <button className={button} onClick={load}>
              {t('Retry')}
            </button>
          </p>
        ) : !data ? (
          <p role="status" className="me-report-state">
            {t('Loading report...')}
          </p>
        ) : (
          <>
            {exportError && (
              <p role="alert" className="me-report-message me-report-error">
                {t(exportError)}
              </p>
            )}
            {data.total > 0 && totals.length > 0 && (
              <dl className="me-report-totals">
                {totals.map(([label, value]) => (
                  <div key={label}>
                    <dt>
                      {t(
                        label === 'amount' && allAmountsUnavailable
                          ? 'Saved amount (USD)'
                          : label === 'amount' && Number(data.totals?.amountUnavailable) > 0
                            ? 'Recorded subtotal (USD)'
                            : TOTAL_LABELS[label] || label
                      )}
                    </dt>
                    <dd>
                      {label === 'amount' && allAmountsUnavailable
                        ? t('Amount unavailable')
                        : label === 'units' && dataset === 'orders' && allAmountsUnavailable
                          ? t('Unavailable')
                          : dataset === 'cash_bank' && label === 'netAmount'
                            ? cashReportMoney(value)
                            : display(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {data.total > 0 && dataset === 'cash_bank' && (
              <p className="me-report-message">
                {t('Cash movement in the selected records is not the current available balance.')}
              </p>
            )}
            {data.total > 0 && dataset === 'orders' && (
              <p className="me-report-message">
                {t(
                  'Selected amounts include all matching order statuses; they are not recognized revenue.'
                )}
              </p>
            )}
            {!data.total ? (
              <div className="me-report-state me-report-empty">
                <span className="material-symbols-outlined me-report-empty-icon" aria-hidden="true">
                  search_off
                </span>
                <p>
                  {t(
                    hasFilters
                      ? 'No records match these filters.'
                      : 'No records are available for this report yet.'
                  )}
                </p>
                {(hasFilters || pendingFilters) && (
                  <button className={button} onClick={resetFilters}>
                    {t('Clear filters')}
                  </button>
                )}
              </div>
            ) : (
              <DistributorReportTable
                dataset={dataset}
                data={data}
                page={page}
                t={t}
                statusLabels={STATUS_LABELS}
              />
            )}
          </>
        )}
        <div className="me-report-pagination" hidden={Boolean(data && !data.total)}>
          <span>
            {data
              ? t('Showing {start}–{end} of {total}', {
                  start: (page - 1) * data.limit + 1,
                  end: Math.min(page * data.limit, data.total),
                  total: data.total,
                })
              : '—'}
          </span>
          {(!data || data.total > data.limit) && (
            <nav aria-label={t('Report pages')}>
              <button
                className={button}
                aria-disabled={!data || page <= 1}
                onClick={() => {
                  if (data && page > 1) setPage(page - 1);
                }}
              >
                {t('Previous')}
              </button>
              <span>
                {data
                  ? t('Page {page} of {pages}', {
                      page,
                      pages: Math.max(1, Math.ceil(data.total / data.limit)),
                    })
                  : '—'}
              </span>
              <button
                className={button}
                aria-disabled={!data || page * data.limit >= data.total}
                onClick={() => {
                  if (data && page * data.limit < data.total) setPage(page + 1);
                }}
              >
                {t('Next')}
              </button>
            </nav>
          )}
        </div>
      </section>
    </section>
  );
}
